import type { CentralIconBaseProps } from '@central-icons-react/round-filled-radius-1-stroke-2/CentralIconBase';
import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';

import type { FeatureName } from '~/shared/feature-flag-service/feature-flag-service';
import type { Translate, TranslationKey } from '~/shared/internationalization/translation';
import {
    IconAgents,
    IconAiTransformation,
    IconAlignment,
    IconAnalyticsDashboard,
    IconControlRoom,
    IconDashboard,
    IconDocument,
    IconFolder,
    IconGlobe,
    IconGroup,
    IconHomeOutlined,
    IconHumanInWindow,
    IconJobArchitecture,
    IconJobProfile,
    IconLearning,
    IconLegacyDashboard,
    IconLevel,
    IconPerson,
    IconSettings,
    IconSkills,
    IconTalentDirectory,
    IconTasks,
} from '~/ui/core/icons';

import { agentsRoute } from '../../../agents';
import { aiTransformationRoute, isAiTransformationStrategyReleased } from '../../../ai-transformation';
import { companyHomeFeatureFlags, companyHomeRoute } from '../../../company-home/route-config';
import { knowledgeBaseRoute, usersAndRolesRoute } from '../../../company-settings/route-config';
import { graphCanvasRoute, isCanvasReleased } from '../../../graph-canvas';
import { growthRoute } from '../../../growth';
import { jobProfileManagementV2Route } from '../../../job-architecture-v2';
import { learningOpportunitiesV2Route } from '../../../learning-opportunities-v2';
import { UnreadNotificationsDot } from '../../../notifications';
import { personalHomeRoute } from '../../../personal-home';
import { positionAlignmentRoute } from '../../../position-alignment';
import { positionManagementRoute } from '../../../position-management';
import { route as dashboardPageRoute } from '../../pages/dashboard/constants';
import { route as dashboardV2Route } from '../../pages/dashboard-v2/constants';
import { route as documentationPageRoute } from '../../pages/documentation/constants';
import { route as jobsPageRoute } from '../../pages/job-list/constants';
import { route as myGrowthPageRoute } from '../../pages/my-growth/constants';
import { route as talentDirectoryPageRoute } from '../../pages/talent-directory/constants';

type NavigationIcon = FunctionComponent<CentralIconBaseProps>;

export type NavigationShortcutKey =
    | 'a'
    | 'b'
    | 'c'
    | 'd'
    | 'e'
    | 'f'
    | 'g'
    | 'h'
    | 'i'
    | 'j'
    | 'k'
    | 'l'
    | 'm'
    | 'n'
    | 'o'
    | 'p'
    | 'q'
    | 'r'
    | 's'
    | 't'
    | 'u'
    | 'v'
    | 'w'
    | 'x'
    | 'y'
    | 'z';

// Everything the spec table may consult: capabilities, feature flags, and the
// dynamic values that feed context-derived paths.
export type NavigationContext = {
    readonly canAccessBusinessIntelligence: boolean;
    readonly canAccessTalentDirectory: boolean;
    readonly canAddToGraphCanvas: boolean;
    readonly canEnterPositions: boolean;
    readonly canManageApiKeys: boolean;
    /** Reading positions without entering Position Management, as a transformation lead does. */
    readonly canReadPositions: boolean;
    readonly canReadSkillCatalog: boolean;
    readonly canSeeNewDashboard: boolean;
    readonly canSeeTalentDashboard: boolean;
    readonly canUseControlRoom: boolean;
    readonly canUserFilterLearningOpportunities: boolean;
    readonly canUserGetJobArchitecture: boolean;
    readonly canUserGetJobProfiles: boolean;
    readonly canUserManageJobs: boolean;
    /** Leading at least one direct report, which opens the Teams scope. */
    readonly hasTeam: boolean;
    readonly isAssumedTenant: boolean;
    readonly isFeatureFlagEnabled: (featureFlagName: FeatureName) => boolean;
    readonly isJobArchitect: boolean;
    readonly isTransformationLead: boolean;
    readonly showGrowthPage: boolean;
    readonly targetArchitectureId: string | null;
    readonly tenantId: string | null;
};

// Where the workspace navigation files an item: the Me or Teams scope, one of
// the Company groups, or the footer that stays the same in every scope.
export type WorkspaceSection =
    | 'architecture'
    | 'footer'
    | 'insights'
    | 'me'
    | 'people'
    | 'talent'
    | 'team'
    | 'transformation';

export type NavigationItemSpec = {
    // Active only on its own path, not on the pages below it.
    readonly end?: boolean;
    readonly externalUrl?: boolean;
    readonly Icon: NavigationIcon;
    // Rendered inside the icon container, which is positioned relative.
    readonly IconAdornment?: FunctionComponent;
    readonly isVisible: (context: NavigationContext) => boolean;
    readonly labelKey: TranslationKey;
    // The side navigation's name for the entry, where it differs from the page's own title.
    readonly menuLabelKey?: TranslationKey;
    readonly path: string | ((context: NavigationContext) => string);
    readonly shortcutKey?: NavigationShortcutKey;
    readonly workspaceSection: WorkspaceSection;
};

// A spec resolved against a context: what SideDrawerLinkV2 renders and what
// useNavigationShortcuts binds.
export type NavigationItem = {
    readonly end: boolean;
    readonly externalUrl: boolean;
    readonly Icon: NavigationIcon;
    readonly IconAdornment?: FunctionComponent;
    readonly label: string;
    readonly labelKey: TranslationKey;
    readonly menuLabel: string;
    readonly path: string;
    readonly shortcutKey?: NavigationShortcutKey;
    readonly workspaceSection: WorkspaceSection;
};

// Order defines the rendered menu order. Shortcut keys must be unique across
// the whole table (asserted in navigation-items.test.ts) because visibility
// can vary per user while the `g <key>` chords share one namespace.
export const navigationItemSpecs: readonly NavigationItemSpec[] = [
    {
        // Company pages such as /company/users live below Home's path without being part of it.
        end: true,
        Icon: IconHomeOutlined,
        // Home shows an area only for a module the user can open: the Knowledge Base through their API keys, the job
        // architecture, the talent directory, or AI transformation through the released strategy, as transformation
        // leads open it without the job architecture.
        isVisible: ({
            canAccessTalentDirectory,
            canManageApiKeys,
            canReadPositions,
            canUserGetJobArchitecture,
            isFeatureFlagEnabled,
            isTransformationLead,
        }) =>
            companyHomeFeatureFlags.every((flag) => isFeatureFlagEnabled(flag)) &&
            (canManageApiKeys ||
                canUserGetJobArchitecture ||
                canAccessTalentDirectory ||
                (isAiTransformationStrategyReleased(isFeatureFlagEnabled) &&
                    (canReadPositions || isTransformationLead))),
        labelKey: 'navigation.home',
        path: companyHomeRoute.patterns.companyHome,
        workspaceSection: 'insights',
    },
    {
        Icon: IconFolder,
        // Moved out of the organization settings, which only job architects open.
        isVisible: ({ isFeatureFlagEnabled, isJobArchitect }) =>
            isJobArchitect && isFeatureFlagEnabled('workspace-navigation'),
        labelKey: 'navigation.knowledge-base',
        path: knowledgeBaseRoute.patterns.knowledgeBase,
        workspaceSection: 'insights',
    },
    {
        Icon: IconDashboard,
        IconAdornment: UnreadNotificationsDot,
        isVisible: ({ canSeeNewDashboard }) => canSeeNewDashboard,
        labelKey: 'navigation.dashboard',
        path: dashboardV2Route.patterns.dashboardV2,
        shortcutKey: 'd',
        workspaceSection: 'architecture',
    },
    {
        Icon: IconHomeOutlined,
        isVisible: ({ isFeatureFlagEnabled }) => isFeatureFlagEnabled('personal-home'),
        labelKey: 'navigation.home',
        path: personalHomeRoute.patterns.personalHome,
        workspaceSection: 'me',
    },
    {
        Icon: IconLegacyDashboard,
        isVisible: ({ canSeeTalentDashboard, isFeatureFlagEnabled }) =>
            canSeeTalentDashboard && !isFeatureFlagEnabled('demo-menu'),
        labelKey: 'navigation.talent-dashboard',
        path: dashboardPageRoute.patterns.dashboard,
        shortcutKey: 'h',
        workspaceSection: 'me',
    },
    {
        Icon: IconDocument,
        isVisible: () => true,
        labelKey: 'navigation.documentation',
        path: documentationPageRoute.patterns.documentation,
        shortcutKey: 'c',
        workspaceSection: 'footer',
    },
    {
        Icon: IconLearning,
        isVisible: ({ isFeatureFlagEnabled, showGrowthPage }) => showGrowthPage && !isFeatureFlagEnabled('demo-menu'),
        labelKey: 'navigation.my-growth',
        path: myGrowthPageRoute.patterns.myGrowth,
        shortcutKey: 'w',
        workspaceSection: 'me',
    },
    {
        Icon: IconLevel,
        isVisible: ({ isFeatureFlagEnabled }) => isFeatureFlagEnabled('growth-goals'),
        labelKey: 'navigation.growth-goals',
        path: growthRoute.patterns.growth,
        shortcutKey: 'k',
        workspaceSection: 'me',
    },
    {
        Icon: IconJobProfile,
        isVisible: ({ isFeatureFlagEnabled }) => isFeatureFlagEnabled('position-alignment'),
        labelKey: 'navigation.alignment',
        path: positionAlignmentRoute.patterns.me,
        workspaceSection: 'me',
    },
    {
        Icon: IconAnalyticsDashboard,
        isVisible: ({ canAccessBusinessIntelligence }) => canAccessBusinessIntelligence,
        labelKey: 'navigation.analytics-dashboard',
        path: '/business-intelligence',
        shortcutKey: 'b',
        workspaceSection: 'talent',
    },
    {
        Icon: IconHumanInWindow,
        isVisible: ({ canUserManageJobs }) => canUserManageJobs,
        labelKey: 'navigation.job-postings',
        path: jobsPageRoute.patterns.jobs,
        shortcutKey: 'o',
        workspaceSection: 'talent',
    },
    {
        Icon: IconJobProfile,
        isVisible: ({ canUserGetJobProfiles }) => canUserGetJobProfiles,
        labelKey: 'navigation.job-profiles',
        path: jobProfileManagementV2Route.patterns.jobProfileManagementV2,
        shortcutKey: 'p',
        workspaceSection: 'architecture',
    },
    {
        Icon: IconLearning,
        isVisible: ({ canUserFilterLearningOpportunities }) => canUserFilterLearningOpportunities,
        labelKey: 'navigation.learning-management',
        path: learningOpportunitiesV2Route.patterns.learningOpportunities,
        shortcutKey: 'l',
        workspaceSection: 'talent',
    },
    {
        Icon: IconTalentDirectory,
        isVisible: ({ canAccessTalentDirectory }) => canAccessTalentDirectory,
        labelKey: 'navigation.talent-directory',
        path: talentDirectoryPageRoute.patterns.talentDirectory,
        shortcutKey: 't',
        workspaceSection: 'talent',
    },
    {
        Icon: IconLevel,
        isVisible: ({ canUserGetJobArchitecture, isFeatureFlagEnabled }) =>
            canUserGetJobArchitecture && isFeatureFlagEnabled('leveling-framework-menu-item'),
        labelKey: 'navigation.career-frameworks',
        path: '/career-frameworks',
        shortcutKey: 'f',
        workspaceSection: 'architecture',
    },
    {
        Icon: IconJobArchitecture,
        isVisible: ({ canUserGetJobArchitecture }) => canUserGetJobArchitecture,
        labelKey: 'navigation.job-architecture',
        path: ({ targetArchitectureId }) =>
            `/job-architecture-v2${is.nonEmptyString(targetArchitectureId) ? `/${targetArchitectureId}` : ''}`,
        shortcutKey: 'j',
        workspaceSection: 'architecture',
    },
    {
        Icon: IconSkills,
        isVisible: ({ canReadSkillCatalog, canUserGetJobArchitecture, isFeatureFlagEnabled }) =>
            canUserGetJobArchitecture && canReadSkillCatalog && isFeatureFlagEnabled('skills-section'),
        labelKey: 'navigation.skills',
        menuLabelKey: 'navigation.menu.skills',
        path: '/skills',
        shortcutKey: 's',
        workspaceSection: 'architecture',
    },
    {
        Icon: IconGlobe,
        isVisible: ({ canAddToGraphCanvas, isFeatureFlagEnabled }) =>
            canAddToGraphCanvas && isCanvasReleased(isFeatureFlagEnabled),
        labelKey: 'navigation.graph-canvas',
        path: graphCanvasRoute.patterns.graphCanvas,
        workspaceSection: 'architecture',
    },
    {
        Icon: IconTasks,
        isVisible: ({ canUserGetJobArchitecture, isFeatureFlagEnabled, isTransformationLead }) =>
            (canUserGetJobArchitecture || isTransformationLead) && isFeatureFlagEnabled('structured-profile-tasks'),
        labelKey: 'navigation.tasks',
        path: '/tasks',
        shortcutKey: 'a',
        workspaceSection: 'architecture',
    },
    {
        Icon: IconAgents,
        // Lead-only users never read the job architecture, so the architecture-wide library stays hidden for them.
        isVisible: ({ canUserGetJobArchitecture, isFeatureFlagEnabled }) =>
            canUserGetJobArchitecture && isFeatureFlagEnabled('agent-attributions'),
        labelKey: 'navigation.agents',
        path: agentsRoute.patterns.agents,
        workspaceSection: 'architecture',
    },
    {
        Icon: IconAiTransformation,
        // The released strategy opens this entry to those who read positions and to every transformation lead, even
        // one with no position assigned yet; everyone else keeps the opportunity map.
        isVisible: ({ canReadPositions, canUserGetJobArchitecture, isFeatureFlagEnabled, isTransformationLead }) =>
            (isAiTransformationStrategyReleased(isFeatureFlagEnabled) && (canReadPositions || isTransformationLead)) ||
            (canUserGetJobArchitecture && isFeatureFlagEnabled('structured-profile-tasks')),
        labelKey: 'navigation.ai-transformation',
        path: aiTransformationRoute.patterns.aiTransformation,
        shortcutKey: 'i',
        workspaceSection: 'transformation',
    },
    {
        Icon: IconGroup,
        isVisible: ({ canEnterPositions, canUserGetJobArchitecture, isFeatureFlagEnabled }) =>
            (canUserGetJobArchitecture || canEnterPositions) && isFeatureFlagEnabled('position-management-v-2'),
        labelKey: 'navigation.position-management',
        menuLabelKey: 'navigation.menu.positions',
        path: positionManagementRoute.patterns.positionManagement,
        shortcutKey: 'm',
        workspaceSection: 'architecture',
    },
    {
        externalUrl: true,
        Icon: IconControlRoom,
        isVisible: ({ canUseControlRoom, isFeatureFlagEnabled, tenantId }) =>
            canUseControlRoom && is.nonEmptyString(tenantId) && !isFeatureFlagEnabled('demo-menu'),
        labelKey: 'navigation.control-room',
        path: ({ isAssumedTenant, tenantId }) =>
            `https://control-room.be.cobrainer.cloud/?tenantId=${tenantId ?? ''}${isAssumedTenant ? '&assumeTenant=True' : ''}`,
        shortcutKey: 'r',
        workspaceSection: 'footer',
    },
    {
        Icon: IconAlignment,
        isVisible: ({ hasTeam, isFeatureFlagEnabled }) => hasTeam && isFeatureFlagEnabled('position-alignment'),
        labelKey: 'navigation.team-alignment',
        path: positionAlignmentRoute.patterns.team,
        workspaceSection: 'team',
    },
    {
        Icon: IconPerson,
        isVisible: ({ isFeatureFlagEnabled, isJobArchitect }) =>
            isJobArchitect && isFeatureFlagEnabled('workspace-navigation'),
        labelKey: 'navigation.users-and-roles',
        path: usersAndRolesRoute.patterns.usersAndRoles,
        shortcutKey: 'u',
        workspaceSection: 'people',
    },
    {
        Icon: IconSettings,
        isVisible: () => true,
        labelKey: 'common.settings',
        path: '/companySettings',
        shortcutKey: 'e',
        workspaceSection: 'footer',
    },
];

export const resolveNavigationItems = (
    specs: readonly NavigationItemSpec[],
    context: NavigationContext,
    translate: Translate,
): readonly NavigationItem[] =>
    specs
        .filter((spec) => spec.isVisible(context))
        .map(
            ({
                end,
                externalUrl,
                Icon,
                IconAdornment,
                labelKey,
                menuLabelKey,
                path,
                shortcutKey,
                workspaceSection,
            }) => ({
                end: end ?? false,
                externalUrl: externalUrl ?? false,
                Icon,
                IconAdornment,
                label: translate(labelKey),
                labelKey,
                menuLabel: translate(menuLabelKey ?? labelKey),
                path: is.string(path) ? path : path(context),
                shortcutKey,
                workspaceSection,
            }),
        );

/**
 * React key that survives path changes. The Job Architecture path gains the architecture id once it loads, and a
 * path key would remount the link then, dropping a hover or focus already on it.
 */
export const navigationItemKey = ({
    labelKey,
    workspaceSection,
}: Pick<NavigationItem, 'labelKey' | 'workspaceSection'>): string => `${workspaceSection}:${labelKey}`;

/** The entry a group header opens instead of listing it among the group's rows. */
export const groupLandingKeys: Partial<Record<WorkspaceSection, TranslationKey>> = {
    architecture: 'navigation.dashboard',
    talent: 'navigation.talent-directory',
};

/** The workspace navigation's order inside a section, where it differs from the table's; unlisted entries follow in table order. */
const workspaceSectionOrder: Partial<Record<WorkspaceSection, readonly TranslationKey[]>> = {
    architecture: [
        'navigation.job-architecture',
        'navigation.position-management',
        'navigation.career-frameworks',
        'navigation.skills',
        'navigation.tasks',
        'navigation.agents',
        'navigation.graph-canvas',
    ],
};

/** A section's entries in menu order: its landing first, then the section's own order, then the rest as listed. */
export const orderSectionItems = <T extends Pick<NavigationItem, 'labelKey'>>(
    section: WorkspaceSection,
    items: readonly T[],
): readonly T[] => {
    const landingKey = groupLandingKeys[section];
    const order = [...(landingKey === undefined ? [] : [landingKey]), ...(workspaceSectionOrder[section] ?? [])];
    const rank = (item: T): number => {
        const index = order.indexOf(item.labelKey);

        return index === -1 ? order.length : index;
    };

    return items.toSorted((left, right) => rank(left) - rank(right));
};

/** Where the logo leads: Home for job architects who can open it, the start page for everyone else. */
export const logoPath = (items: readonly Pick<NavigationItem, 'path'>[], isJobArchitect: boolean): string =>
    isJobArchitect && items.some(({ path }) => path === companyHomeRoute.patterns.companyHome)
        ? companyHomeRoute.patterns.companyHome
        : '/';

/** Whether the item leads to the current page: its own path, or a page below it unless the item ends at its path. */
export const isNavigationItemActive = (
    pathname: string,
    { end, path }: Pick<NavigationItem, 'end' | 'path'>,
): boolean => pathname === path || (!end && pathname.startsWith(`${path}/`));
